import React from "react";
import rehypeReact from "rehype-react";
import type { Node } from "unist";
import { visit } from "unist-util-visit";
import { ExternalLink } from "~/atoms/ExternalLink";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";
import BgQuoteLeftSvg from "~/images/bgQuoteLeft.svg";
import BgQuoteRightSvg from "~/images/bgQuoteRight.svg";

export const Paragraph: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .paragraph {
          color: ${colors.defaultText};
          margin: 20px 0 0;
        }
        .paragraph:first-child {
          margin: 0;
        }
        .paragraph :global(strong) {
          font-weight: 700;
        }
        @media (${breakPoints.sp}) {
          .paragraph {
            font-size: 15px;
          }
        }
        @media (${breakPoints.pc}) {
          .paragraph {
            font-size: 16px;
          }
        }
      `}</style>
      <p className="paragraph">{node.children}</p>
    </>
  );
};

export const Heading1: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading1 {
          color: ${colors.brand};
          margin: 30px 0;
          font-weight: 700;
        }
        .heading1:first-child {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .heading1 {
            font-size: 24px;
          }
        }
        @media (${breakPoints.pc}) {
          .heading1 {
            font-size: 28px;
          }
        }
      `}</style>
      <h1 className="heading1">{node.children}</h1>
    </>
  );
};

export const Heading2: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading2 {
          border-left: solid 3px ${colors.brand};
          color: ${colors.brand};
          margin: 30px 0;
          padding: 0 0 0 20px;
        }
        .heading2:first-child {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .heading2 {
            font-size: 21px;
          }
        }
        @media (${breakPoints.pc}) {
          .heading2 {
            font-size: 24px;
          }
        }
      `}</style>
      <h2 className="heading2">{node.children}</h2>
    </>
  );
};

export const Heading3: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading3 {
          color: ${colors.defaultText};
          border-bottom: solid 1px ${colors.defaultText};
          margin: 30px 0;
          padding: 0 0 10px;
        }
        .heading3:first-child {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .heading3 {
            font-size: 18px;
          }
        }
        @media (${breakPoints.pc}) {
          .heading3 {
            font-size: 21px;
          }
        }
      `}</style>
      <h3 className="heading3">{node.children}</h3>
    </>
  );
};

export const Heading4: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .heading4 {
          color: ${colors.defaultText};
          border-bottom: solid 2px ${colors.gray3};
          margin: 30px 0;
          padding: 0 0 10px;
          font-weight: 500;
        }
        .heading4:first-child {
          margin: 0;
        }
        @media (${breakPoints.sp}) {
          .heading4 {
            font-size: 16px;
          }
        }
        @media (${breakPoints.pc}) {
          .heading4 {
            font-size: 18px;
          }
        }
      `}</style>
      <h4 className="heading4">{node.children}</h4>
    </>
  );
};

export const StyledUl: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledUl {
          color: ${colors.defaultText};
          margin: 40px 0;
          padding: 0 0 0 30px;
          list-style: disc;
          font-size: 16px;
          font-weight: 400;
        }
        .styledUl :global(li) {
          margin-top: 10px;
        }
      `}</style>
      <ul className="styledUl">{node.children}</ul>
    </>
  );
};

export const StyledOl: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledOl {
          color: ${colors.defaultText};
          margin: 40px 0;
          padding: 0 0 0 30px;
          list-style: number;
          font-size: 16px;
          font-weight: 400;
        }
        .styledOl :global(li) {
          margin-top: 10px;
        }
      `}</style>
      <ol className="styledOl">{node.children}</ol>
    </>
  );
};

export const Blockquote: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .blockquote {
          background: url(${BgQuoteLeftSvg}), url(${BgQuoteRightSvg}), ${colors.creamGray};
          background-position: 15px 15px, calc(100% - 15px) calc(100% - 15px);
          color: ${colors.defaultText};
          background-repeat: no-repeat;
          border-radius: 5px;
          font-style: italic;
          margin: 20px auto;
          padding: 20px 30px;
          font-size: 15px;
        }
      `}</style>
      <blockquote className="blockquote">{node.children}</blockquote>
    </>
  );
};

export const StyledImg: React.FC<Node> = (node) => {
  return (
    <>
      <style jsx>{`
        .styledImg {
          display: block;
          height: auto;
          width: 100%;
          margin: 20px auto;
        }
      `}</style>
      <img className="styledImg" src={node.src} alt={node?.alt || ""} loading="lazy" />
    </>
  );
};

export const HyperLink: React.FC<Node & { href: string }> = ({ href, children }) => {
  return (
    <>
      <style jsx>{`
        :global(.externalLink) {
          color: ${colors.brand};
        }
        :global(.externalLink:hover) {
          text-decoration: underline;
        }
      `}</style>
      <ExternalLink className="externalLink" to={href} target="_blank">
        {children}
      </ExternalLink>
    </>
  );
};

export const nl2br = (tree) => {
  visit(tree, (node, index, parent) => {
    if (node.type !== "text") return node;
    if (parent?.tagName !== "p") return node;

    const values = node.value.trim().split("\n");
    if (values.length > 1) {
      const children = values
        .map((value, index) => {
          const childNode =
            index === 0
              ? [{ type: "text", value }]
              : [
                  { type: "element", tagName: "br" },
                  { type: "text", value },
                ];
          return childNode;
        })
        .flat();
      const newChildren = [...parent.children.slice(0, index), ...children, ...parent.children.slice(index + 1)];
      parent.children = newChildren;
    }
  });
  return tree;
};

export const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: {
    h1: Heading1,
    h2: Heading2,
    h3: Heading3,
    h4: Heading4,
    h5: Heading4,
    h6: Heading4,
    p: Paragraph,
    a: HyperLink,
    ul: StyledUl,
    ol: StyledOl,
    blockquote: Blockquote,
    img: StyledImg,
  },
}).Compiler;

export const renderHtmlAst = (htmlAst) => renderAst(nl2br(htmlAst));
